import React, { useCallback, useEffect, useMemo, useState } from 'react'
import axios from 'axios'
import toast from 'react-hot-toast'
import { useNavigate } from 'react-router-dom'
import AdminLayout from '../../components/Admin/AdminLayout'
import AdminModal from '../../components/Admin/AdminModal'
import { resolveIconNode, RsIcon, IconLabel } from '../../components/icons'

function StatCard({ icon, cls, label, value, sub }) {
  return (
    <div className="admin-stat-card flex">
      <span className={`admin-stat-icon ${cls}`}>{resolveIconNode(icon, { size: 'md' })}</span>
      <div className="admin-stat-info">
        <div className="label">{label}</div>
        <div className="admin-stat-value sm">{value}</div>
        {sub != null && sub !== '' && <div className="sub">{sub}</div>}
      </div>
    </div>
  )
}

const TYPE_META = {
  gp: { label: 'GP', icon: 'fund', cls: 'gp' },
  lp: { label: 'LP', icon: 'briefcase', cls: 'lp' },
  startup: { label: 'Startup', icon: 'upgrade', cls: 'startup' },
}

function formatDate(d) {
  if (!d) return '—'
  return new Date(d).toISOString().slice(0, 10)
}

function amountBucket(amount) {
  const n = Number(amount) || 0
  if (n >= 10e6) return 'large'
  if (n >= 1e6) return 'medium'
  return 'small'
}

const PAGE_SIZE = 8

export default function AdminListings() {
  const navigate = useNavigate()
  const [stats, setStats] = useState(null)
  const [listings, setListings] = useState([])
  const [typeFilter, setTypeFilter] = useState('all')
  const [statusFilter, setStatusFilter] = useState('all')
  const [amountFilter, setAmountFilter] = useState('all')
  const [search, setSearch] = useState('')
  const [page, setPage] = useState(1)
  const [reviewItem, setReviewItem] = useState(null)
  const [adminNote, setAdminNote] = useState('')
  const [busy, setBusy] = useState(false)

  const load = useCallback(() => {
    axios.get('/api/admin/listings')
      .then((r) => {
        setStats(r.data.stats)
        setListings(r.data.listings || [])
      })
      .catch(() => toast.error('Failed to load'))
  }, [])

  useEffect(() => { load() }, [load])

  const filtered = useMemo(() => listings.filter((l) => {
    if (typeFilter !== 'all' && l.listing_type !== typeFilter) return false
    if (statusFilter !== 'all' && l.review_status !== statusFilter) return false
    if (amountFilter === 'large' && amountBucket(l.amount) !== 'large') return false
    if (amountFilter === 'medium' && amountBucket(l.amount) !== 'medium') return false
    if (amountFilter === 'small' && amountBucket(l.amount) !== 'small') return false
    if (search) {
      const q = search.toLowerCase()
      if (!l.title?.toLowerCase().includes(q)
        && !l.publisher_name?.toLowerCase().includes(q)
        && !l.publisher_company?.toLowerCase().includes(q)) return false
    }
    return true
  }), [listings, typeFilter, statusFilter, amountFilter, search])

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE))
  const pageItems = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE)

  useEffect(() => { setPage(1) }, [typeFilter, statusFilter, amountFilter, search])

  const resetFilters = () => {
    setTypeFilter('all')
    setStatusFilter('all')
    setAmountFilter('all')
    setSearch('')
  }

  const openPublisherChat = (item) => {
    if (!item?.publisher_id) {
      toast.error('Publisher account not found')
      return
    }
    navigate(`/admin/chat?contact=${item.publisher_id}`)
  }

  const review = async (item, decision) => {
    setBusy(true)
    try {
      await axios.post(`/api/admin/listings/${item.source}/${item.id}/review`, {
        decision,
        note: adminNote || undefined,
      })
      toast.success(decision === 'approved' ? 'Listing approved' : 'Rejected')
      setReviewItem(null)
      setAdminNote('')
      load()
    } catch {
      toast.error('Operation failed')
    } finally {
      setBusy(false)
    }
  }

  const pendingCount = stats?.pendingReview ?? listings.filter((l) => l.review_status === 'pending').length

  return (
    <AdminLayout title="Deal Listing" subtitle="Review and approve listings">
      <div className="admin-stats-row cols-5">
        <StatCard icon="clock" cls="orange" label="Pending Review" value={stats?.pendingReview ?? 0} sub="Awaiting decision" />
        <StatCard icon="fund" cls="purple" label="GP Fundraising" value={stats?.gpFundraising ?? 0} sub="Need review" />
        <StatCard icon="briefcase" cls="red" label="LP Asset Sale" value={stats?.lpAssetSale ?? 0} sub="Need review" />
        <StatCard icon="upgrade" cls="blue" label="Startup Funding" value={stats?.startupFunding ?? 0} sub="Need review" />
        <StatCard icon="check" cls="green" label="Approved Today" value={stats?.approvedToday ?? 0} sub={`${stats?.approvedDelta >= 0 ? '+' : ''}${stats?.approvedDelta ?? 0} from yesterday`} />
      </div>

      <div className="admin-alert-banner">
        <span className="admin-alert-icon"><RsIcon name="alertTriangle" size="md" /></span>
        <div className="admin-alert-content">
          <h3>{pendingCount} listings pending review — only approved posts appear on Find a Deal</h3>
          <p>Listings from GP, LP, and Startup are hidden from counterparties until you approve them.</p>
        </div>
      </div>

      <div className="admin-filter-bar">
        <span style={{ fontSize: 11.5, fontWeight: 600, color: 'var(--admin-text-sec)' }}>Filter:</span>
        <select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)}>
          <option value="all">All Types</option>
          <option value="gp">GP — Fundraising</option>
          <option value="lp">LP — Asset Sale</option>
          <option value="startup">Startup — Funding</option>
        </select>
        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
          <option value="all">All Status</option>
          <option value="pending">Pending</option>
          <option value="approved">Approved</option>
          <option value="rejected">Rejected</option>
        </select>
        <select value={amountFilter} onChange={(e) => setAmountFilter(e.target.value)}>
          <option value="all">All Amounts</option>
          <option value="large">&gt; $10M</option>
          <option value="medium">$1M – $10M</option>
          <option value="small">&lt; $1M</option>
        </select>
        <input type="text" placeholder="Search listing title or publisher..." value={search} onChange={(e) => setSearch(e.target.value)} />
        <span className="admin-filter-spacer" />
        <button type="button" className="admin-btn admin-btn-sm admin-btn-ghost" onClick={resetFilters}>Reset</button>
      </div>

      <div className="admin-table-card">
        <table>
          <thead>
            <tr>
              <th style={{ width: '22%' }}>Listing</th>
              <th style={{ width: '8%' }}>Type</th>
              <th style={{ width: '15%' }}>Publisher</th>
              <th style={{ width: '14%' }}>Amount</th>
              <th style={{ width: '9%' }}>Submitted</th>
              <th style={{ width: '9%' }}>Status</th>
              <th className="admin-th-actions">Actions</th>
            </tr>
          </thead>
          <tbody>
            {pageItems.map((l) => {
              const typeMeta = TYPE_META[l.listing_type] || TYPE_META.gp
              return (
                <tr key={`${l.source}-${l.id}`} className={l.review_status === 'pending' ? 'row-pending' : ''}>
                  <td>
                    <div className="admin-listing-cell">
                      <span className="title">{l.title}</span>
                      <span className="preview">{l.description}</span>
                    </div>
                  </td>
                  <td>
                    <span className={`admin-type-badge ${typeMeta.cls}`}>
                      <IconLabel name={typeMeta.icon} size="xs">{typeMeta.label}</IconLabel>
                    </span>
                  </td>
                  <td>
                    <div className="admin-publisher-cell">
                      <span className="name">{l.publisher_name || '—'}</span>
                      <span className="company">{l.publisher_company || '—'}</span>
                    </div>
                  </td>
                  <td>
                    <span className={`admin-amount-chip ${l.review_status === 'approved' ? 'raised' : ''}`}>{l.amount_label}</span>
                  </td>
                  <td><span style={{ fontSize: 12, color: 'var(--admin-text-muted)' }}>{formatDate(l.submitted_at)}</span></td>
                  <td>
                    <span className={`admin-listing-status ${l.review_status}`}>
                      {l.review_status === 'pending' ? 'Pending' : l.review_status === 'approved' ? 'Approved' : 'Rejected'}
                    </span>
                  </td>
                  <td className="admin-td-actions">
                    <div className="admin-action-btns">
                      <button type="button" className="admin-btn admin-btn-sm admin-btn-ghost" onClick={() => { setReviewItem(l); setAdminNote('') }}>
                        <IconLabel name="review" size="xs">Review</IconLabel>
                      </button>
                      {l.publisher_id && (
                        <button type="button" className="admin-btn admin-btn-sm admin-btn-ghost" onClick={() => openPublisherChat(l)}>
                          <IconLabel name="chat" size="xs">Chat</IconLabel>
                        </button>
                      )}
                      {l.review_status === 'pending' && (
                        <>
                          <button type="button" className="admin-btn admin-btn-sm admin-btn-success" disabled={busy} onClick={() => review(l, 'approved')}>
                            <IconLabel name="check" size="xs">Approve</IconLabel>
                          </button>
                          <button type="button" className="admin-btn admin-btn-sm admin-btn-danger-ghost" disabled={busy} onClick={() => review(l, 'rejected')}>
                            <IconLabel name="xCircle" size="xs">Reject</IconLabel>
                          </button>
                        </>
                      )}
                      {l.review_status === 'approved' && (
                        <button type="button" className="admin-btn admin-btn-sm admin-btn-danger-ghost" disabled={busy} onClick={() => review(l, 'rejected')}>↩ Withdraw</button>
                      )}
                    </div>
                  </td>
                </tr>
              )
            })}
            {!pageItems.length && (
              <tr><td colSpan={7} style={{ textAlign: 'center', padding: 32, color: 'var(--admin-text-muted)' }}>No listings match filters</td></tr>
            )}
          </tbody>
        </table>

        {reviewItem && (
          <AdminModal
            open
            wide
            title={`Review: ${reviewItem.title}`}
            subtitle={`${TYPE_META[reviewItem.listing_type]?.label} · Submitted ${formatDate(reviewItem.submitted_at)} · ${reviewItem.review_status}`}
            onClose={() => setReviewItem(null)}
            footer={(
              <>
                <div className="admin-review-footer-left" style={{ flex: 1, marginRight: 16 }}>
                  <label htmlFor="adminNote">Admin Note (optional):</label>
                  <textarea id="adminNote" rows={2} value={adminNote} onChange={(e) => setAdminNote(e.target.value)} placeholder="Add a note for the publisher..." />
                </div>
                <div className="admin-review-footer-right" style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  {reviewItem.publisher_id && (
                    <button type="button" className="admin-btn secondary" onClick={() => openPublisherChat(reviewItem)}>
                      Chat with Publisher
                    </button>
                  )}
                  {reviewItem.review_status === 'pending' && (
                    <>
                      <button type="button" className="admin-btn" style={{ background: '#f56c6c', color: '#fff' }} disabled={busy} onClick={() => review(reviewItem, 'rejected')}>
                        <IconLabel name="xCircle" size="xs">Reject</IconLabel>
                      </button>
                      <button type="button" className="admin-btn success" disabled={busy} onClick={() => review(reviewItem, 'approved')}>
                        <IconLabel name="check" size="xs">Approve &amp; Publish</IconLabel>
                      </button>
                    </>
                  )}
                  <button type="button" className="admin-btn secondary" onClick={() => setReviewItem(null)}>Close</button>
                </div>
              </>
            )}
          >
            <div className="admin-review-body" style={{ padding: 0, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
              <div className="admin-review-section">
                <div className="admin-review-section-title">Listing Details</div>
                <div className="admin-review-field"><span className="field-label">Listing Title</span><span className="field-value">{reviewItem.title}</span></div>
                <div className="admin-review-field"><span className="field-label">Description</span><span className="field-value">{reviewItem.description || '—'}</span></div>
                <div className="admin-review-field"><span className="field-label">Amount</span><span className="field-value success">{reviewItem.amount_label}</span></div>
                <div className="admin-review-field"><span className="field-label">Sector</span><span className="field-value">{reviewItem.industry || '—'}</span></div>
              </div>
              <div className="admin-review-section">
                <div className="admin-review-section-title">Publisher Info</div>
                <div className="admin-review-field"><span className="field-label">Name</span><span className="field-value">{reviewItem.publisher_name}</span></div>
                <div className="admin-review-field"><span className="field-label">Company</span><span className="field-value">{reviewItem.publisher_company}</span></div>
                <div className="admin-review-field"><span className="field-label">Role</span><span className="field-value">{reviewItem.publisher_role}</span></div>
                <div className="admin-review-field"><span className="field-label">Email</span><span className="field-value">{reviewItem.publisher_email}</span></div>
                <div className="admin-review-field">
                  <span className="field-label">Company Verified</span>
                  <span className={`field-value ${reviewItem.company_verified === 'verified' ? 'success' : 'warning'}`}>
                    {reviewItem.company_verified === 'verified' ? 'Verified' : 'Pending'}
                  </span>
                </div>
              </div>
            </div>
          </AdminModal>
        )}

        <div className="admin-pagination">
          <div className="admin-pagination-info">
            Showing {filtered.length ? (page - 1) * PAGE_SIZE + 1 : 0}–{Math.min(page * PAGE_SIZE, filtered.length)} of {filtered.length} listings
          </div>
          <div className="admin-pagination-controls">
            <button type="button" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>‹</button>
            {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => i + 1).map((n) => (
              <button key={n} type="button" className={page === n ? 'active' : ''} onClick={() => setPage(n)}>{n}</button>
            ))}
            <button type="button" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}>›</button>
          </div>
        </div>
      </div>
    </AdminLayout>
  )
}
